import { ROOT_CONTEXT, SpanKind, SpanStatusCode, Tracer } from "@opentelemetry/api";
import {
  MetricType,
  MetricWithAttribution,
  onCLS,
  onFCP,
  onINP,
  onLCP,
  onTTFB,
  TTFBAttribution,
} from "web-vitals";

import CONV from "../semantic-conventions";
import type { RootSpan } from "../types";
import { getFoldedPath } from "../utils";
import { log } from "../utils/logger";

export class WebVitals {
  private tracer: Tracer;
  private viewId?: string;

  constructor(tracer: Tracer) {
    this.tracer = tracer;
  }

  public subscribe(rootSpan?: RootSpan) {
    this.viewId = rootSpan?.spanContext().traceId;
    log("[L9RUM WebVitals] Subscribing to Web Vitals metrics");
    onCLS(this._reportMetric, { reportAllChanges: true });
    onFCP(this._reportMetric);
    onINP(this._reportMetric, { reportAllChanges: true });
    onLCP(this._reportMetric, { reportAllChanges: true });
    onTTFB(this._reportMetric);
  }

  public unsubscribe() {
    this.viewId = undefined;
  }

  private _reportMetric = (metric: MetricType) => {
    const timestamp = Math.floor(Date.now() / 1000);
    const attrs = this._getSpanAttributes(metric, timestamp);
    if (this.viewId) {
      attrs["view.id"] = this.viewId;
    }

    const spanName = `Web Vital - ${metric.name}`;
    const metricSpan = this.tracer.startSpan(
      spanName,
      {
        kind: SpanKind.INTERNAL,
        attributes: attrs,
      },
      ROOT_CONTEXT,
    );
    metricSpan.setStatus({ code: SpanStatusCode.OK });
    metricSpan.end();

    log("[L9RUM WebVitals] Metric span created", metric);
  };

  private _getSpanAttributes(metric: MetricType | MetricWithAttribution, timestamp: number) {
    const metricName = metric.name.toLowerCase();
    const metricPrefix = `web_vital.${metricName}`;
    const commonAttributes = {
      "web_vital.navigation_type": metric.navigationType,
    };
    const pageAttributes = this._getPageAttributes();
    const attributes: { [key: string]: string | number } = {
      ...commonAttributes,
      ...pageAttributes,
      [`${metricPrefix}.id`]: metric.id,
      [`${metricPrefix}.value`]: metric.value,
      [`${metricPrefix}.timestamp`]: timestamp,
      [`${metricPrefix}.rating`]: metric.rating,
      [`${metricPrefix}.delta`]: metric.delta,
    };

    if (metric.entries && metric.entries.length > 0) {
      attributes[`${metricPrefix}.entries_count`] = metric.entries.length;

      if (metric.name === "TTFB" && metric.entries[0]) {
        const entry = metric.entries[0];
        attributes[`${metricPrefix}.start_time`] = entry.startTime;
        attributes[`${metricPrefix}.duration`] = entry.duration;
        attributes[`${metricPrefix}.fetch_start`] = entry.fetchStart;
        attributes[`${metricPrefix}.response_start`] = entry.responseStart;
        attributes[`${metricPrefix}.request_start`] = entry.requestStart;
      }
    }

    const metricWithAttribution = metric as MetricWithAttribution;

    if (metricWithAttribution.attribution) {
      if (metric.name === "TTFB") {
        const attribution = metricWithAttribution.attribution as TTFBAttribution;
        attributes[`${metricPrefix}.waiting_duration`] = attribution.waitingDuration;
        attributes[`${metricPrefix}.dns_duration`] = attribution.dnsDuration;
        attributes[`${metricPrefix}.connection_duration`] = attribution.connectionDuration;
        attributes[`${metricPrefix}.request_duration`] = attribution.requestDuration;
      }
    }

    return attributes;
  }

  private _getPageAttributes(): Record<string, string> {
    if (typeof window === "undefined") {
      return {};
    }

    const { location } = window;

    return {
      origin: location.origin,
      path: getFoldedPath(location.pathname),
      [CONV.PAGE_URL]: location.href,
      [CONV.PAGE_HASH]: location.hash,
      [CONV.PAGE_ROUTE]: location.pathname,
      [CONV.PAGE_HOSTNAME]: location.hostname,
      [CONV.PAGE_SEARCH]: location.search,
      [CONV.URL_PATH]: location.pathname,
    };
  }
}
